import React, { useState } from 'react';
import { CompleteKundali, PlanetName, ZodiacSign, Language } from '../types/kundali';
import { BookOpen, BarChart3, Info, Grid, Award, Star, ChevronDown, ChevronUp, Layers, Compass, Zap, ShieldAlert, Sparkles, Sliders } from 'lucide-react';
import { calculatePrastaraTables, calculateShodhanaAndPindas, generateAshtakvargaPredictions } from '../utils/ashtakvargaAdvancedEngine';
import { ZODIAC_SIGNS, getSavBySign, getSavByBhava, getBavBySign, getBavByBhava } from '../utils/vedicEngine';
import { getPlanetFullName, getPlanetShortCode, getZodiacName } from '../utils/language';

interface AshtakvargaViewProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const AshtakvargaView: React.FC<AshtakvargaViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  const { ashtakvarga, houses, planets, shadbala, gochar } = kundali;
  const [selectedPlanet, setSelectedPlanet] = useState<PlanetName>('Jupiter');
  const [prastaraPlanet, setPrastaraPlanet] = useState<PlanetName>('Jupiter');
  const [isAdvancedMode, setIsAdvancedMode] = useState<boolean>(false);
  const [showHowToReadSav, setShowHowToReadSav] = useState<boolean>(false);
  const [expandedPrediction, setExpandedPrediction] = useState<string | null>(null);
  const [showPrastaraExplain, setShowPrastaraExplain] = useState<boolean>(false);

  const planetOptions: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

  // SAV calculations & validation
  const savScores = ashtakvarga.sarvashtakvarga || new Array(12).fill(28);
  const totalSav = savScores.reduce((a, b) => a + b, 0);
  const avgSav = Number((totalSav / 12).toFixed(1));
  const isSavValid = totalSav === 337;

  // Strongest and weakest houses (explicitly Bhava-resolved)
  const houseWithScores = houses.map((h) => ({
    ...h,
    sav: getSavByBhava(ashtakvarga, h.houseNumber, houses)
  })).sort((a, b) => b.sav - a.sav);

  const strongestHouses = houseWithScores.slice(0, 3);
  const weakestHouses = [...houseWithScores].reverse().slice(0, 3);

  // Selected planet data
  const selectedPlanetPos = planets.find(p => p.name === selectedPlanet);
  const selectedPlanetShadbala = shadbala?.planets.find(s => s.planet === selectedPlanet);
  const planetBhinnaList = (ashtakvarga.bhinna as any)[selectedPlanet] || new Array(12).fill(4);
  const natalBindus = selectedPlanetPos ? getBavBySign(ashtakvarga, selectedPlanet, selectedPlanetPos.signIndex) : 4;

  // Prastara & Shodhana advanced data
  const prastaraTables = calculatePrastaraTables(kundali);
  const shodhanaResults = calculateShodhanaAndPindas(kundali);
  const predictions = generateAshtakvargaPredictions(kundali);

  // Current Transit Activation (Saturn & Jupiter from Gochar or ephemeris)
  const transitSaturn = gochar?.items.find(i => i.planet === 'Saturn') || { currentSign: 'Aquarius', houseFromLagna: 10, isRetrograde: false };
  const transitJupiter = gochar?.items.find(i => i.planet === 'Jupiter') || { currentSign: 'Taurus', houseFromLagna: 2, isRetrograde: false };

  const saturnSignIdx = ZODIAC_SIGNS.indexOf(transitSaturn.currentSign as ZodiacSign);
  const saturnSav = saturnSignIdx >= 0 ? getSavBySign(ashtakvarga, saturnSignIdx) : 28;
  const saturnBav = saturnSignIdx >= 0 ? getBavBySign(ashtakvarga, 'Saturn', saturnSignIdx) : 4;

  const jupiterSignIdx = ZODIAC_SIGNS.indexOf(transitJupiter.currentSign as ZodiacSign);
  const jupiterSav = jupiterSignIdx >= 0 ? getSavBySign(ashtakvarga, jupiterSignIdx) : 28;
  const jupiterBav = jupiterSignIdx >= 0 ? getBavBySign(ashtakvarga, 'Jupiter', jupiterSignIdx) : 4;

  // Color helpers
  const getSavColorStyle = (score: number) => {
    if (score >= 32) return { text: 'text-emerald-700 font-extrabold', bg: 'bg-emerald-50/80 border-emerald-200', bar: 'bg-emerald-600', label: isHi ? 'अति शुभ (>=32)' : 'Strong (>=32)' };
    if (score >= 28) return { text: 'text-astro-primary font-bold', bg: 'bg-astro-card border-astro-border', bar: 'bg-astro-primary', label: isHi ? 'औसत (28-31)' : 'Average (28-31)' };
    if (score >= 25) return { text: 'text-[#52635F] font-bold', bg: 'bg-astro-secondary-surface/60 border-[#E2DED5]', bar: 'bg-astro-secondary', label: isHi ? 'औसत से कम (25-27)' : 'Below Avg (25-27)' };
    return { text: 'text-rose-700 font-extrabold', bg: 'bg-rose-50/80 border-rose-200', bar: 'bg-rose-600', label: isHi ? 'कमजोर (<25)' : 'Weak (<25)' };
  };

  const getBavColorStyle = (pt: number) => {
    if (pt >= 5) return { text: 'text-emerald-700 font-bold', badge: 'bg-emerald-100 text-emerald-900 border-emerald-300', label: isHi ? 'शुभ (5+)' : 'Above Average (5+)' };
    if (pt === 4) return { text: 'text-astro-text font-semibold', badge: 'bg-astro-card text-astro-secondary-text border-astro-border', label: isHi ? 'सामान्य (4)' : 'Average (4)' };
    return { text: 'text-rose-700 font-bold', badge: 'bg-rose-100 text-rose-900 border-rose-300', label: isHi ? 'कम (<4)' : 'Below Average (<=3)' };
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Top Mode Toggle Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-astro-primary text-white flex items-center justify-center shadow-sm">
            <Compass className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif font-bold text-astro-primary text-lg">Classical Ashtakvarga & Transit Timing System</h2>
            <p className="text-xs text-astro-secondary-text">BPHS 8-Contributor Sarvashtakvarga, Bhinna Ashtakvarga, Shodhana & Predictive Timing</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <div className="text-xs text-astro-secondary-text hidden md:block">
            SAV Total: <span className="font-mono font-bold text-astro-primary">{totalSav}</span> {isSavValid ? '(Valid 337)' : '(Custom)'}
          </div>
          <button
            onClick={() => setIsAdvancedMode(!isAdvancedMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-xs ${
              isAdvancedMode
                ? 'bg-astro-primary text-white shadow-md'
                : 'bg-astro-card text-astro-primary border border-astro-border hover:bg-astro-border/50'
            }`}
          >
            <Sliders className="w-4 h-4" />
            {isAdvancedMode ? 'Advanced Mode Active' : 'Switch to Advanced Mode'}
          </button>
        </div>
      </div>

      {/* 1. Ashtakvarga Summary Card */}
      <div className="bg-gradient-to-br from-astro-primary to-astro-text text-white rounded-2xl p-6 shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Sparkles className="w-36 h-36" />
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-amber-300" />
          <h3 className="font-serif font-bold text-lg text-amber-200 tracking-wide">Ashtakvarga Executive Summary</h3>
        </div>
        <p className="text-sm text-astro-border leading-relaxed mb-4">
          Your strongest Ashtakvarga support is currently centered in the <span className="text-white font-bold">House {strongestHouses[0].houseNumber} ({strongestHouses[0].sign})</span> with <span className="text-amber-300 font-mono font-bold">{strongestHouses[0].sav} bindus</span>, indicating robust capacity for success and resilience when activated by favorable transits and dashas.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-white/10 text-xs">
          <div>
            <span className="text-white/60 block">Strongest House</span>
            <span className="font-bold text-white text-sm">H{strongestHouses[0].houseNumber} ({strongestHouses[0].sign}) - {strongestHouses[0].sav} Pts</span>
          </div>
          <div>
            <span className="text-white/60 block">Areas Requiring Care</span>
            <span className="font-bold text-amber-200 text-sm">H{weakestHouses[0].houseNumber} ({weakestHouses[0].sign}) - {weakestHouses[0].sav} Pts</span>
          </div>
          <div>
            <span className="text-white/60 block">Active Saturn Transit</span>
            <span className="font-bold text-white text-sm">{transitSaturn.currentSign} (SAV {saturnSav})</span>
          </div>
          <div>
            <span className="text-white/60 block">Active Jupiter Transit</span>
            <span className="font-bold text-white text-sm">{transitJupiter.currentSign} (SAV {jupiterSav})</span>
          </div>
        </div>
      </div>

      {/* 2. Sarvashtakvarga (SAV) Section */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-astro-primary" />
            <div>
              <h3 className="font-bold text-astro-primary font-serif text-lg">Sarvashtakvarga (12 Houses Total SAV Points)</h3>
              <p className="text-xs text-astro-secondary-text">Total Bindus: <span className="font-mono font-bold">{totalSav}</span> (Average per sign: {avgSav}) • Target baseline: 28</p>
            </div>
          </div>
          <button
            onClick={() => setShowHowToReadSav(!showHowToReadSav)}
            className="flex items-center gap-1.5 text-xs text-astro-primary bg-astro-card px-3 py-1.5 rounded-lg border border-astro-border font-semibold hover:bg-astro-border/50"
          >
            <Info className="w-4 h-4" /> How to Read SAV {showHowToReadSav ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
          </button>
        </div>

        {showHowToReadSav && (
          <div className="mb-4 bg-astro-card border border-astro-border rounded-xl p-4 text-xs text-astro-secondary-text space-y-2">
            <h4 className="font-bold text-astro-primary text-sm">Interpreting Sarvashtakvarga (SAV) Scores</h4>
            <p>
              Sarvashtakvarga combines the bindus contributed by all 7 planets and the Lagna across the 12 houses. A score of <span className="font-bold text-astro-primary">28</span> is average. Houses with <span className="font-bold text-emerald-700">32+ points</span> are exceptionally strong and yield auspicious results easily. Houses with <span className="font-bold text-rose-700">under 25 points</span> indicate areas where conscious effort, patience, and remedial support are beneficial.
            </p>
            <p className="italic text-[11px]">
              Note: Ashtakvarga is a timing and strength tool. High SAV must be cross-referenced with planetary dignity, lordship, current dasha, and transits.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {houses.map((h) => {
            const score = getSavByBhava(ashtakvarga, h.houseNumber, houses);
            const style = getSavColorStyle(score);

            return (
              <div
                key={h.houseNumber}
                className={`p-3 rounded-xl border flex flex-col justify-between space-y-2 transition-all ${style.bg}`}
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-astro-primary">House {h.houseNumber}</span>
                  <span className="text-[10px] text-astro-primary font-serif font-bold">R{h.signIndex + 1} {h.sign.slice(0, 3)}</span>
                </div>

                <div className="text-center my-1">
                  <span className={`text-2xl font-extrabold font-mono ${style.text}`}>{score}</span>
                  <span className="text-[10px] text-astro-secondary-text block font-medium">{style.label}</span>
                </div>

                <div className="w-full bg-astro-border h-2 rounded-full overflow-hidden border border-astro-border">
                  <div
                    className={`h-full ${style.bar} transition-all duration-500`}
                    style={{ width: `${Math.min(100, (score / 40) * 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Strongest & Weakest Areas Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3 border-b border-astro-border pb-2">
            <Award className="w-5 h-5 text-emerald-700" />
            <h3 className="font-bold text-astro-primary font-serif text-base">Strongest Areas (Top 3 Houses)</h3>
          </div>
          <div className="space-y-3">
            {strongestHouses.map((h, i) => (
              <div key={h.houseNumber} className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold text-xs">{i + 1}</span>
                    <h4 className="font-bold text-astro-primary text-sm">House {h.houseNumber} ({h.sign})</h4>
                  </div>
                  <p className="text-xs text-astro-secondary-text mt-1">
                    Ruled by <span className="font-semibold text-astro-text">{h.signLord}</span>. Strongly supports initiative, growth, and favorable outcomes when activated.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-extrabold text-emerald-700 font-mono">{h.sav}</span>
                  <span className="text-[10px] text-astro-secondary-text block">Bindus</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3 border-b border-astro-border pb-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-astro-primary font-serif text-base">Areas Requiring Care (Bottom 3 Houses)</h3>
          </div>
          <div className="space-y-3">
            {weakestHouses.map((h, i) => (
              <div key={h.houseNumber} className="bg-astro-secondary-surface/50 border border-[#E2DED5] rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-astro-secondary text-white flex items-center justify-center font-bold text-xs">{i + 1}</span>
                    <h4 className="font-bold text-astro-primary text-sm">House {h.houseNumber} ({h.sign})</h4>
                  </div>
                  <p className="text-xs text-astro-secondary-text mt-1">
                    Ruled by <span className="font-semibold text-astro-text">{h.signLord}</span>. Benefits from conscious effort, patience, and supportive remedial measures.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-extrabold text-[#52635F] font-mono">{h.sav}</span>
                  <span className="text-[10px] text-astro-secondary-text block">Bindus</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4. Bhinnashtakavarga (BAV) - Compact Matrix & Detailed Inspector */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <Grid className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-lg">
              Bhinnashtakavarga (BAV) Master Table Matrix (7 Planets × 12 Signs)
            </h3>
          </div>
          <span className="text-xs text-astro-secondary-text">★ Highlights Natal Rashi Placement</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-center text-xs border-collapse font-mono">
            <thead>
              <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] bg-astro-card">
                <th className="py-3 px-3 text-left font-sans font-bold text-astro-primary">Planet</th>
                {ZODIAC_SIGNS.map((sign, sIdx) => (
                  <th key={sign} className="py-3 px-2">
                    <span className="block text-astro-primary font-bold">R{sIdx + 1}</span>
                    <span className="text-[9px] text-astro-secondary-text block font-serif font-bold">{sign.slice(0, 3)}</span>
                  </th>
                ))}
                <th className="py-3 px-3 text-right font-sans font-bold text-astro-primary">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border">
              {planetOptions.map((pName) => {
                const bPoints = (ashtakvarga.bhinna as any)[pName] || new Array(12).fill(4);
                const totalP = bPoints.reduce((acc: number, val: number) => acc + val, 0);
                const pPos = planets.find(p => p.name === pName);

                return (
                  <tr key={pName} className="hover:bg-astro-card transition-colors">
                    <td className="py-2.5 px-3 text-left font-sans font-bold text-astro-primary flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-astro-primary" />
                      {pName}
                    </td>
                    {bPoints.map((pt: number, sIdx: number) => {
                      const isNatalSign = pPos?.signIndex === sIdx;
                      const bavStyle = getBavColorStyle(pt);

                      return (
                        <td
                          key={sIdx}
                          className={`py-2.5 px-2 relative transition-all ${bavStyle.text} ${
                            isNatalSign ? 'bg-astro-secondary/15/90 ring-2 ring-amber-400 font-extrabold shadow-inner' : ''
                          }`}
                          title={`${pName} in ${ZODIAC_SIGNS[sIdx]}: ${pt} bindus (${bavStyle.label})${isNatalSign ? ' [NATAL RASHI]' : ''}`}
                        >
                          <span className={`inline-block px-1.5 py-0.5 rounded ${isNatalSign ? 'bg-astro-secondary/20 text-[#263633] font-black' : ''}`}>
                            {pt}
                          </span>
                          {isNatalSign && (
                            <span className="absolute -top-1 right-0 text-[8px] bg-astro-accent text-white px-1 rounded-full shadow-xs">★</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-2.5 px-3 text-right font-bold text-astro-primary font-sans">
                      {totalP}
                    </td>
                  </tr>
                );
              })}

              <tr className="bg-astro-card font-bold border-t-2 border-astro-border">
                <td className="py-3 px-3 text-left text-astro-accent font-sans font-bold">
                  Total SAV (Bindus)
                </td>
                {savScores.map((score, sIdx) => {
                  const style = getSavColorStyle(score);
                  return (
                    <td key={sIdx} className={`py-3 px-2 text-sm font-bold ${style.text}`}>
                      {score}
                    </td>
                  );
                })}
                <td className="py-3 px-3 text-right text-astro-accent text-sm font-sans font-extrabold">
                  {totalSav}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Planet BAV Inspector */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-lg">
              Detailed Bhinna Ashtakvarga & Transit Relevance Inspector
            </h3>
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto no-scrollbar">
            {planetOptions.map((p) => (
              <button
                key={p}
                onClick={() => setSelectedPlanet(p)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedPlanet === p
                    ? 'bg-astro-primary text-white font-bold shadow-sm'
                    : 'bg-astro-card text-astro-secondary-text border border-astro-border hover:text-astro-text'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {selectedPlanetPos && (
          <div className="mb-5 bg-astro-card border border-astro-border rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-astro-primary text-white flex items-center justify-center font-serif text-lg font-bold shadow-sm">
                {selectedPlanet[0]}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-astro-primary text-base font-serif">{selectedPlanet} Natal Status</h4>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                    selectedPlanetPos.dignity === 'Exalted' ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                    selectedPlanetPos.dignity === 'Debilitated' ? 'bg-rose-100 text-rose-900 border-rose-300' :
                    selectedPlanetPos.dignity === 'Own House' ? 'bg-teal-50 text-astro-primary border-teal-300' :
                    'bg-astro-bg text-astro-secondary-text border-astro-border'
                  }`}>
                    {selectedPlanetPos.dignity}
                  </span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-0.5">
                  Placed in <span className="font-bold text-astro-text">{selectedPlanetPos.sign}</span> (House {selectedPlanetPos.house}) • Degree: {selectedPlanetPos.degreeFormatted}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-astro-bg px-4 py-2.5 rounded-xl border border-astro-border w-full md:w-auto justify-around">
              <div className="text-center">
                <span className="text-[10px] text-astro-secondary-text block uppercase">BAV in Natal Sign</span>
                <span className={`text-lg font-bold font-mono ${getBavColorStyle(natalBindus).text}`}>
                  {natalBindus} / 8
                </span>
              </div>
              <div className="h-8 w-px bg-astro-border" />
              <div className="text-center">
                <span className="text-[10px] text-astro-secondary-text block uppercase">Shadbala</span>
                <span className="text-lg font-bold text-astro-accent font-mono">
                  {selectedPlanetShadbala ? `${selectedPlanetShadbala.totalRupas.toFixed(2)} Rupas` : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {ZODIAC_SIGNS.map((sign, sIdx) => {
            const pt = planetBhinnaList[sIdx] || 4;
            const isNatalSign = selectedPlanetPos?.signIndex === sIdx;
            const bavStyle = getBavColorStyle(pt);

            return (
              <div
                key={sign}
                className={`p-3 rounded-xl border transition-all flex flex-col justify-between space-y-1 relative ${
                  isNatalSign
                    ? 'bg-astro-secondary-surface/95 border-astro-accent shadow-md ring-2 ring-amber-400'
                    : 'bg-astro-card border-astro-border'
                }`}
              >
                {isNatalSign && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-astro-accent text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1 whitespace-nowrap">
                    <Star className="w-3 h-3 fill-white" /> Natal Rashi
                  </div>
                )}
                <div className={`text-[11px] font-semibold flex items-center justify-between ${isNatalSign ? 'mt-2 text-astro-accent font-bold' : 'text-astro-secondary-text'}`}>
                  <span>R{sIdx + 1}</span>
                  <span className="font-serif font-bold">{sign}</span>
                </div>
                <div className="text-center py-1">
                  <span className={`text-2xl font-extrabold font-mono ${bavStyle.text}`}>{pt}</span>
                  <span className="text-[10px] text-astro-secondary-text block">Bindus / 8</span>
                </div>
                <div className="text-[10px] text-center pt-1 border-t border-astro-border/50">
                  <span className={`font-semibold px-2 py-0.5 rounded-full border text-[9px] inline-block ${bavStyle.badge}`}>
                    {bavStyle.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 5. Current Transit Activation Section */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
          <Zap className="w-5 h-5 text-amber-600" />
          <h3 className="font-bold text-astro-primary font-serif text-lg">What Is Activated Now? (Current Transits)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-astro-card border border-astro-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-astro-primary text-sm">Saturn Transit</span>
              <span className="text-xs bg-astro-secondary/15 text-astro-secondary px-2 py-0.5 rounded font-bold border border-[#E2DED5]">
                {transitSaturn.currentSign} (House {transitSaturn.houseFromLagna})
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mb-3">
              Saturn transiting <span className="font-bold text-astro-text">{transitSaturn.currentSign}</span> has an SAV score of <span className="font-bold font-mono text-astro-primary">{saturnSav}</span> and Saturn BAV score of <span className="font-bold font-mono text-astro-primary">{saturnBav} / 8</span>.
            </p>
            <div className="text-[11px] bg-astro-bg p-2.5 rounded-lg border border-astro-border text-astro-primary">
              <span className="font-bold block mb-0.5">Interpretation:</span>
              {saturnBav >= 4 ? 'Favorable transit support. Disciplined efforts in career and duties yield stable, long-term rewards.' : 'Requires diligence and patience in professional or structural undertakings.'}
            </div>
          </div>

          <div className="bg-astro-card border border-astro-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-astro-primary text-sm">Jupiter Transit</span>
              <span className="text-xs bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded font-bold border border-emerald-300">
                {transitJupiter.currentSign} (House {transitJupiter.houseFromLagna})
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mb-3">
              Jupiter transiting <span className="font-bold text-astro-text">{transitJupiter.currentSign}</span> has an SAV score of <span className="font-bold font-mono text-astro-primary">{jupiterSav}</span> and Jupiter BAV score of <span className="font-bold font-mono text-astro-primary">{jupiterBav} / 8</span>.
            </p>
            <div className="text-[11px] bg-astro-bg p-2.5 rounded-lg border border-astro-border text-astro-primary">
              <span className="font-bold block mb-0.5">Interpretation:</span>
              {jupiterBav >= 5 ? 'Strong benefic expansion. Favorable period for financial growth, wisdom, and opportunities.' : 'Moderate growth period; focus on steady learning and consolidation.'}
            </div>
          </div>
        </div>
      </div>

      {/* 6. Ashtakvarga Predictions Engine */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
          <Sparkles className="w-5 h-5 text-astro-primary" />
          <h3 className="font-bold text-astro-primary font-serif text-lg">Ashtakvarga Predictive Insights & Domain Analysis</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {predictions.map((p) => {
            const isExpanded = expandedPrediction === p.category;
            return (
              <div key={p.category} className="bg-astro-card border border-astro-border rounded-xl p-4 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-astro-accent">{p.category}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold border ${
                      p.supportLevel === 'Strongly Supported' ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                      p.supportLevel === 'Moderately Supported' ? 'bg-teal-50 text-astro-primary border-teal-300' :
                      'bg-astro-secondary/15 text-astro-secondary border-[#E2DED5]'
                    }`}>
                      {p.supportLevel} ({p.confidenceLevel}%)
                    </span>
                  </div>
                  <h4 className="font-serif font-bold text-astro-primary text-base mb-1">{p.title}</h4>
                  <p className="text-xs text-astro-secondary-text">
                    House {p.houseNumber} ({p.sign}) • SAV Score: <span className="font-mono font-bold text-astro-primary">{p.savScore}</span>
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-astro-border">
                  <button
                    onClick={() => setExpandedPrediction(isExpanded ? null : p.category)}
                    className="flex items-center justify-between w-full text-xs font-bold text-astro-primary hover:text-astro-accent transition-colors"
                  >
                    <span>Why? (Ashtakvarga & Lordship Breakdown)</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {isExpanded && (
                    <div className="mt-3 space-y-1.5 text-[11px] text-astro-secondary-text bg-astro-bg p-3 rounded-lg border border-astro-border">
                      <p>• {p.whyExplanation.houseSav}</p>
                      <p>• {p.whyExplanation.houseLord}</p>
                      <p>• {p.whyExplanation.bavDetails}</p>
                      <p>• {p.whyExplanation.transitNote}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 7. Advanced Mode Sections (Prastara, Shodhana, Shodhya Pinda) */}
      {isAdvancedMode && (
        <div className="space-y-6 pt-4 border-t-2 border-astro-border">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-astro-accent" />
            <h3 className="font-serif font-bold text-astro-primary text-xl">Advanced Ashtakvarga Calculations (BPHS Classical Suite)</h3>
          </div>

          {/* Prastara Ashtakvarga */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 border-b border-astro-border pb-3">
              <div>
                <h4 className="font-bold text-astro-primary font-serif text-base">Prastara Ashtakvarga (8-Contributor Breakdown)</h4>
                <p className="text-xs text-astro-secondary-text">Inspect individual contributor bindus (0 or 1) across the 12 signs</p>
              </div>
              <div className="flex items-center gap-2">
                {planetOptions.map(p => (
                  <button
                    key={p}
                    onClick={() => setPrastaraPlanet(p)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      prastaraPlanet === p ? 'bg-astro-primary text-white font-bold' : 'bg-astro-card text-astro-secondary-text border border-astro-border'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-center text-xs border-collapse font-mono">
                <thead>
                  <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] bg-astro-card">
                    <th className="py-2.5 px-3 text-left font-sans font-bold text-astro-primary">Contributor</th>
                    {ZODIAC_SIGNS.map(s => (
                      <th key={s} className="py-2.5 px-1.5">{s.slice(0, 3)}</th>
                    ))}
                    <th className="py-2.5 px-3 text-right font-sans font-bold text-astro-primary">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border">
                  {Object.entries(prastaraTables[prastaraPlanet]?.contributors || {}).map(([contrib, arr]) => {
                    const rowTotal = arr.reduce((a, b) => a + b, 0);
                    return (
                      <tr key={contrib} className="hover:bg-astro-card">
                        <td className="py-2 px-3 text-left font-sans font-bold text-astro-primary">{contrib}</td>
                        {arr.map((val, idx) => (
                          <td key={idx} className={`py-2 px-1.5 ${val === 1 ? 'text-astro-primary font-bold bg-teal-50/50' : 'text-astro-secondary-text/50'}`}>
                            {val}
                          </td>
                        ))}
                        <td className="py-2 px-3 text-right font-bold text-astro-primary font-sans">{rowTotal}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-astro-card font-bold border-t-2 border-astro-border">
                    <td className="py-2.5 px-3 text-left text-astro-accent font-sans">Total BAV</td>
                    {prastaraTables[prastaraPlanet]?.signTotals.map((tot, idx) => (
                      <td key={idx} className="py-2.5 px-1.5 text-astro-accent font-extrabold">{tot}</td>
                    ))}
                    <td className="py-2.5 px-3 text-right text-astro-accent font-sans font-extrabold">
                      {prastaraTables[prastaraPlanet]?.signTotals.reduce((a, b) => a + b, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Shodhana & Shodhya Pinda */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
            <h4 className="font-bold text-astro-primary font-serif text-base mb-2">Trikona Shodhana, Ekadhipatya Shodhana & Shodhya Pinda</h4>
            <p className="text-xs text-astro-secondary-text mb-4">
              Classical reduction process (Triad reduction and Lordship reduction) to distill spiritual and material longevity multipliers (Shodhya Pinda).
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-center text-xs border-collapse font-mono">
                <thead>
                  <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] bg-astro-card">
                    <th className="py-3 px-3 text-left font-sans font-bold text-astro-primary">Planet</th>
                    <th className="py-3 px-3">Original BAV Sum</th>
                    <th className="py-3 px-3">Trikona Reduced</th>
                    <th className="py-3 px-3">Ekadhipatya Reduced</th>
                    <th className="py-3 px-3">Rashi Pinda</th>
                    <th className="py-3 px-3">Graha Pinda</th>
                    <th className="py-3 px-3 text-right font-sans font-bold text-astro-primary">Shodhya Pinda</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border">
                  {shodhanaResults.map(res => {
                    const origSum = res.original.reduce((a, b) => a + b, 0);
                    const triSum = res.trikonaReduced.reduce((a, b) => a + b, 0);
                    const ekaSum = res.ekadhipatyaReduced.reduce((a, b) => a + b, 0);

                    return (
                      <tr key={res.planet} className="hover:bg-astro-card">
                        <td className="py-2.5 px-3 text-left font-sans font-bold text-astro-primary">{res.planet}</td>
                        <td className="py-2.5 px-3">{origSum}</td>
                        <td className="py-2.5 px-3">{triSum}</td>
                        <td className="py-2.5 px-3 font-bold text-astro-primary">{ekaSum}</td>
                        <td className="py-2.5 px-3">{res.rashiPinda}</td>
                        <td className="py-2.5 px-3">{res.grahaPinda}</td>
                        <td className="py-2.5 px-3 text-right font-extrabold text-astro-accent font-sans">{res.shodhyaPinda}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 8. House Strength Breakdown Table */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
          <BookOpen className="w-5 h-5 text-astro-primary" />
          <h3 className="font-bold text-astro-primary font-serif text-lg">House Strength Through Ashtakvarga & Classical Significance</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] bg-astro-card">
                <th className="py-3 px-3 font-bold text-astro-primary">House & Sign</th>
                <th className="py-3 px-3 font-bold text-astro-primary">SAV Bindus</th>
                <th className="py-3 px-3 font-bold text-astro-primary">Life Area & Significance</th>
                <th className="py-3 px-3 font-bold text-astro-primary">Classical Interpretation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border">
              {houses.map((h, idx) => {
                const score = getSavByBhava(ashtakvarga, h.houseNumber, houses);
                const style = getSavColorStyle(score);

                const significations = [
                  'Self, vitality, physical appearance & overall life path',
                  'Wealth, accumulated family assets, speech & food habits',
                  'Courage, younger siblings, communication & short travels',
                  'Home, mother, immovable property, vehicles & inner peace',
                  'Progeny, higher intelligence, romance, mantras & speculation',
                  'Health challenges, debts, litigation, daily service & enemies',
                  'Marriage, business partnerships, public dealings & spouse',
                  'Longevity, sudden transformations, obstacles & inheritance',
                  'Fortune, higher dharma, guru, long journeys & higher learning',
                  'Career, professional status, authority & public recognition',
                  'Gains, income streams, network circles & fulfillment of desires',
                  'Expenses, spiritual liberation, foreign residence & isolation'
                ];

                return (
                  <tr key={h.houseNumber} className="hover:bg-astro-card">
                    <td className="py-3 px-3 font-bold text-astro-primary font-serif">
                      H{h.houseNumber} ({h.sign}) <span className="text-[10px] text-astro-secondary-text block font-sans font-normal">Lord: {h.signLord}</span>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-1 rounded font-mono font-bold ${style.bg} ${style.text}`}>
                        {score} pts ({style.label.split(' ')[0]})
                      </span>
                    </td>
                    <td className="py-3 px-3 text-astro-text font-medium">{significations[idx]}</td>
                    <td className="py-3 px-3 text-astro-secondary-text">
                      {score >= 32 ? 'Highly auspicious support; produces strong constructive results.' :
                       score >= 28 ? 'Solid baseline support; steady and reliable outcomes.' :
                       'Requires conscious effort and supportive dasha activation.'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
